"use client"

import { m } from "framer-motion"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { NotFoundBeacon } from "@/components/analytics/not-found-beacon"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"

const SUGGESTED_LINKS = [
  { labelKey: "howItWorks", path: "/how-it-works" },
  { labelKey: "pricing", path: "/pricing" },
  { labelKey: "locations", path: "/locations" },
  { labelKey: "contact", path: "/contact" },
] as const

export default function NotFoundClient() {
  const t = useTranslations("notFound")
  const lp = useLocalePath()

  return (
    <div className="flex flex-1 items-center justify-center px-6 py-24 md:py-32">
      <NotFoundBeacon surface="locale" />
      <div className="mx-auto max-w-xl text-center">
        <p className="text-sm font-medium tracking-wider text-neutral-400 uppercase">
          {t("label")}
        </p>

        <h1 className="text-aubergine-900 mt-4 text-3xl font-normal sm:text-5xl md:text-6xl">
          {t("title")}
        </h1>

        <p className="mx-auto mt-4 max-w-md text-base text-neutral-500">
          {t("description")}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            href={lp("/")}
            className={cn(buttonVariants({ variant: "primary" }))}
          >
            {t("goHome")}
          </Link>
          <Link
            href={lp("/contact")}
            className={cn(buttonVariants({ variant: "outline" }))}
          >
            {t("contactUs")}
          </Link>
        </div>

        <m.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          className="mt-12"
          aria-label={t("popularPages")}
        >
          <p className="mb-4 text-xs font-medium tracking-wider text-neutral-400 uppercase">
            {t("popularPages")}
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-2">
            {SUGGESTED_LINKS.map((link) => (
              <li key={link.path}>
                <Link
                  href={lp(link.path)}
                  className="text-aubergine-900 inline-block rounded-full bg-white px-4 py-2 text-sm transition-colors hover:bg-neutral-100"
                >
                  {t(link.labelKey)}
                </Link>
              </li>
            ))}
          </ul>
        </m.nav>
      </div>
    </div>
  )
}
